import { Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import {
  BottomSheet,
  Button,
  DropdownSelect,
  NumPad,
  type DropdownOption,
} from '../../components';
import { useAppData } from '../../data';
import type {
  ScheduledFrequency,
  ScheduledItem,
  TransactionType,
} from '../../data/types';
import { cn } from '../../lib/cn';
import { toIsoDate } from '../../lib/date';

type Props = {
  /** Open mode: 'add' opens fresh, an existing item opens edit. null = closed. */
  mode: 'add' | ScheduledItem | null;
  /** Pre-fills type for the add flow ('expense' = bill, 'income' = paycheck). */
  type: TransactionType;
  onClose: () => void;
  onSave: (
    input:
      | {
          mode: 'add';
          item: Omit<ScheduledItem, 'id'>;
        }
      | {
          mode: 'edit';
          id: string;
          patch: Partial<ScheduledItem>;
        },
  ) => void;
  onDelete?: (id: string) => void;
};

const FREQUENCY_OPTIONS: DropdownOption[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Biweekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'one_time', label: 'One time' },
];

/**
 * BottomSheet for creating or editing a recurring income/bill. The form
 * stack collects name + frequency + next date + category, then the
 * NumPad commits the amount and saves. For an existing item, an extra
 * Delete button sits below the NumPad.
 */
export function ScheduledItemSheet({
  mode,
  type,
  onClose,
  onSave,
  onDelete,
}: Props) {
  const data = useAppData();

  const editing = mode && mode !== 'add' ? mode : null;
  const open = mode !== null;

  // Filter categories to whichever side this sheet is editing.
  const categories = useMemo(
    () =>
      data.categories
        .filter((c) => c.type === type)
        .sort((a, b) => a.order - b.order),
    [data.categories, type],
  );
  const categoryOptions: DropdownOption[] = useMemo(
    () => categories.map((c) => ({ value: c.id, label: c.name })),
    [categories],
  );

  const todayIso = toIsoDate(new Date());
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState<ScheduledFrequency>('monthly');
  const [nextDate, setNextDate] = useState(todayIso);
  const [categoryId, setCategoryId] = useState('');
  const [amount, setAmount] = useState(0);

  // Reset form when the sheet opens with new context.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setFrequency(editing.frequency);
      setNextDate(editing.nextDate);
      setCategoryId(editing.categoryId);
      setAmount(editing.amount);
    } else {
      setName('');
      setFrequency('monthly');
      setNextDate(todayIso);
      setCategoryId(categories[0]?.id ?? '');
      setAmount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  // If categories load after first render, pin the dropdown.
  useEffect(() => {
    if (open && !categoryId && categories[0]) {
      setCategoryId(categories[0].id);
    }
  }, [open, categoryId, categories]);

  const submit = (committedAmount: number) => {
    if (!name.trim() || !categoryId || committedAmount <= 0) return;
    if (editing) {
      onSave({
        mode: 'edit',
        id: editing.id,
        patch: {
          name: name.trim(),
          amount: committedAmount,
          type,
          categoryId,
          frequency,
          nextDate,
        },
      });
    } else {
      onSave({
        mode: 'add',
        item: {
          name: name.trim(),
          amount: committedAmount,
          type,
          categoryId,
          frequency,
          nextDate,
          active: true,
        },
      });
    }
    onClose();
  };

  const sheetTitle = editing
    ? `Edit ${type === 'expense' ? 'bill' : 'income'}`
    : type === 'expense'
      ? 'Add bill'
      : 'Add income';

  const placeholder =
    type === 'expense' ? 'Rent, internet, gym…' : 'Paycheck, dividend…';

  return (
    <BottomSheet open={open} onClose={onClose} title={sheetTitle}>
      <div className="px-card-pad pt-3 pb-1 flex flex-col gap-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={placeholder}
          autoCapitalize="sentences"
          maxLength={60}
          className="w-full h-11 px-4 rounded-button bg-surface-secondary text-[15px] text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent/40"
        />
        <DropdownSelect
          options={categoryOptions}
          value={categoryId}
          onChange={setCategoryId}
          placeholder="Pick a category"
        />
        <div className="grid grid-cols-2 gap-3">
          <DropdownSelect
            options={FREQUENCY_OPTIONS}
            value={frequency}
            onChange={(v) => setFrequency(v as ScheduledFrequency)}
          />
          <input
            type="date"
            value={nextDate}
            onChange={(e) => setNextDate(e.target.value)}
            className={cn(
              'w-full h-11 px-3 rounded-button bg-surface-secondary',
              'text-[15px] text-text-primary',
              'focus:outline-none focus:ring-2 focus:ring-accent/40',
            )}
          />
        </div>
      </div>

      <NumPad
        initialValue={amount}
        onChange={setAmount}
        onDone={submit}
        doneLabel={editing ? 'Update' : 'Save'}
      />

      {editing && onDelete ? (
        <div className="px-card-pad pb-card-pad pt-1">
          <Button
            variant="destructive"
            fullWidth
            leadingIcon={<Trash2 size={16} strokeWidth={1.5} />}
            onClick={() => {
              onDelete(editing.id);
              onClose();
            }}
          >
            Delete
          </Button>
        </div>
      ) : null}
    </BottomSheet>
  );
}
